// Spells editor. Effects use a discriminated union; form switches fields
// based on the selected effect kind. Master/detail shell delegated to
// `createCatalogueEditor`.

import type { SpellEffect, SpellTemplate } from "@vinyl/shared";
import {
  type CatalogueEditor,
  createCatalogueEditor,
} from "./catalogue-editor.js";
import { el } from "./dom.js";
import { numberField, selectField, textField } from "./form-fields.js";

export interface SpellsScreenCallbacks {
  onDirtyChange: (dirty: boolean) => void;
}

export interface SpellsScreen {
  root: HTMLElement;
  setSpells(spells: readonly SpellTemplate[]): void;
  getDraft(): SpellTemplate[];
  isDirty(): boolean;
  markPublished(): void;
}

const EFFECT_KINDS = [
  "damage",
  "heal",
  "dot",
  "slow",
  "absorb",
  "buffDmg",
] as const;
type EffectKind = (typeof EFFECT_KINDS)[number];

function blank(): SpellTemplate {
  return {
    id: "new_spell",
    name: "New Spell",
    school: "fire",
    castMode: "once",
    requirement: {},
    manaCost: 10,
    castTimeMs: 0,
    cooldownMs: 1500,
    range: 10,
    targeting: "single",
    effects: [{ kind: "damage", basePower: 10 }],
    visualHint: "arcane",
  };
}

function sanitize(s: SpellTemplate): SpellTemplate {
  const req: SpellTemplate["requirement"] = {};
  if (s.requirement.int && s.requirement.int > 0) req.int = s.requirement.int;
  if (s.requirement.fai && s.requirement.fai > 0) req.fai = s.requirement.fai;
  return {
    id: s.id,
    name: s.name,
    school: s.school,
    castMode: s.castMode,
    requirement: req,
    manaCost: s.manaCost,
    castTimeMs: s.castTimeMs,
    cooldownMs: s.cooldownMs,
    range: s.range,
    targeting: s.targeting,
    ...(s.targeting === "aoeGround" && s.aoeRadius
      ? { aoeRadius: s.aoeRadius }
      : {}),
    effects: s.effects.map((e) => ({ ...e })),
    visualHint: s.visualHint,
  };
}

function defaultEffect(kind: EffectKind): SpellEffect {
  switch (kind) {
    case "damage":
      return { kind: "damage", basePower: 10 };
    case "heal":
      return { kind: "heal", basePower: 10 };
    case "dot":
      return { kind: "dot", tickPower: 5, tickIntervalMs: 1000, durationMs: 3000 };
    case "slow":
      return { kind: "slow", speedMult: 0.5, durationMs: 3000 };
    case "absorb":
      return { kind: "absorb", basePool: 50, intScale: 5, durationMs: 10_000 };
    case "buffDmg":
      return { kind: "buffDmg", dmgPct: 0.2, durationMs: 30_000 };
  }
}

export function createSpellsScreen(cb: SpellsScreenCallbacks): SpellsScreen {
  const editor: CatalogueEditor<SpellTemplate> = createCatalogueEditor<SpellTemplate>(
    {
      entityName: "spell",
      getName: (s) => s.name,
      blank,
      sanitize,
      showFilter: false,
      renderEditor: ({ draft, markDirty, rerender }) =>
        renderForm(draft, markDirty, rerender),
    },
    { onDirtyChange: cb.onDirtyChange },
  );

  function renderForm(
    s: SpellTemplate,
    markDirty: () => void,
    rerender: () => void,
  ): HTMLElement {
    return el(
      "div",
      {},
      el("h2", {}, s.name || "(unnamed)"),
      section("Identity", [
        textField("id", s.id, (v) => {
          s.id = v;
          markDirty();
        }),
        textField("Name", s.name, (v) => {
          s.name = v;
          markDirty();
        }),
        selectField(
          "School",
          ["divine", "necromancy", "water", "fire", "air", "earth"],
          s.school,
          (v) => {
            s.school = v as SpellTemplate["school"];
            markDirty();
          },
        ),
        selectField("Cast mode", ["auto", "once"], s.castMode, (v) => {
          s.castMode = v as SpellTemplate["castMode"];
          markDirty();
        }),
        selectField(
          "Visual hint",
          ["fire", "frost", "arcane", "divine"],
          s.visualHint,
          (v) => {
            s.visualHint = v as SpellTemplate["visualHint"];
            markDirty();
          },
        ),
      ]),
      section("Requirements (0 = no req)", [
        numberField("INT", s.requirement.int ?? 0, (v) => {
          s.requirement = { ...s.requirement, int: Math.max(0, Math.floor(v)) };
          if (s.requirement.int === 0) delete s.requirement.int;
          markDirty();
        }),
        numberField("FAI", s.requirement.fai ?? 0, (v) => {
          s.requirement = { ...s.requirement, fai: Math.max(0, Math.floor(v)) };
          if (s.requirement.fai === 0) delete s.requirement.fai;
          markDirty();
        }),
      ]),
      section("Cast pipeline", [
        numberField("Mana cost", s.manaCost, (v) => {
          s.manaCost = Math.max(0, Math.floor(v));
          markDirty();
        }),
        numberField("Cast time (ms)", s.castTimeMs, (v) => {
          s.castTimeMs = Math.max(0, Math.floor(v));
          markDirty();
        }),
        numberField("Cooldown (ms)", s.cooldownMs, (v) => {
          s.cooldownMs = Math.max(0, Math.floor(v));
          markDirty();
        }),
        numberField("Range (tiles)", s.range, (v) => {
          s.range = Math.max(0, Math.floor(v));
          markDirty();
        }),
        selectField(
          "Targeting",
          ["single", "aoeGround", "self"],
          s.targeting,
          (v) => {
            s.targeting = v as SpellTemplate["targeting"];
            markDirty();
            rerender();
          },
        ),
        ...(s.targeting === "aoeGround"
          ? [
              numberField("AOE radius (tiles)", s.aoeRadius ?? 3, (v) => {
                s.aoeRadius = Math.max(0, Math.floor(v));
                markDirty();
              }),
            ]
          : []),
      ]),
      renderEffects(s, markDirty, rerender),
      el(
        "div",
        { class: "editor-actions" },
        el(
          "button",
          {
            class: "button danger",
            onclick: () => {
              if (!confirm(`Delete "${s.name}"?`)) return;
              const next = editor.getDraft().filter((d) => d.id !== s.id);
              editor.set(next);
              cb.onDirtyChange(true);
            },
          },
          "Delete from draft",
        ),
      ),
    );
  }

  function renderEffects(
    s: SpellTemplate,
    markDirty: () => void,
    rerender: () => void,
  ): HTMLElement {
    const rows = el("div", { class: "affix-list" });
    for (let i = 0; i < s.effects.length; i++) {
      rows.append(renderEffectRow(s, i, s.effects[i]!, markDirty, rerender));
    }
    return el(
      "div",
      { class: "editor-section" },
      el("h3", {}, `Effects (${s.effects.length}/5)`),
      rows,
      el(
        "div",
        { class: "affix-actions" },
        el(
          "button",
          {
            class: "button secondary",
            type: "button",
            onclick: () => {
              s.effects = [...s.effects, defaultEffect("damage")];
              markDirty();
              rerender();
            },
          },
          "+ Add effect",
        ),
      ),
    );
  }

  function renderEffectRow(
    s: SpellTemplate,
    idx: number,
    eff: SpellEffect,
    markDirty: () => void,
    rerender: () => void,
  ): HTMLElement {
    const row = el("div", { class: "effect-row" });
    const kindSel = el(
      "select",
      {
        onchange: (e: Event) => {
          const next = defaultEffect((e.target as HTMLSelectElement).value as EffectKind);
          s.effects = s.effects.map((x, i) => (i === idx ? next : x));
          markDirty();
          rerender();
        },
      },
    );
    for (const k of EFFECT_KINDS) {
      const opt = el("option", { value: k }, k);
      if (k === eff.kind) (opt as HTMLOptionElement).selected = true;
      kindSel.append(opt);
    }
    row.append(kindSel);

    // Inline per-kind fields. Keep it minimal: one or two numeric inputs.
    const fields = el("div", { class: "effect-fields" });
    const apply = (patch: Partial<SpellEffect>): void => {
      s.effects = s.effects.map((x, i) => (i === idx ? ({ ...x, ...patch } as SpellEffect) : x));
      markDirty();
    };
    switch (eff.kind) {
      case "damage":
      case "heal":
        fields.append(
          inlineNum("basePower", eff.basePower, (v) => apply({ basePower: v })),
        );
        break;
      case "dot":
        fields.append(
          inlineNum("tickPower", eff.tickPower, (v) => apply({ tickPower: v })),
          inlineNum("tickIntervalMs", eff.tickIntervalMs, (v) => apply({ tickIntervalMs: v })),
          inlineNum("durationMs", eff.durationMs, (v) => apply({ durationMs: v })),
        );
        break;
      case "slow":
        fields.append(
          inlineNum("speedMult (0..1)", eff.speedMult, (v) => apply({ speedMult: v }), 0.05),
          inlineNum("durationMs", eff.durationMs, (v) => apply({ durationMs: v })),
        );
        break;
      case "absorb":
        fields.append(
          inlineNum("basePool", eff.basePool, (v) => apply({ basePool: v })),
          inlineNum("intScale", eff.intScale, (v) => apply({ intScale: v })),
          inlineNum("durationMs", eff.durationMs, (v) => apply({ durationMs: v })),
        );
        break;
      case "buffDmg":
        fields.append(
          inlineNum("dmgPct (0.2 = +20%)", eff.dmgPct, (v) => apply({ dmgPct: v }), 0.05),
          inlineNum("durationMs", eff.durationMs, (v) => apply({ durationMs: v })),
        );
        break;
    }
    row.append(fields);
    row.append(
      el(
        "button",
        {
          class: "button danger",
          type: "button",
          title: "Remove",
          onclick: () => {
            s.effects = s.effects.filter((_, i) => i !== idx);
            markDirty();
            rerender();
          },
        },
        "×",
      ),
    );
    return row;
  }

  return {
    root: editor.root,
    setSpells(spells) {
      editor.set(
        spells.map((s) => ({
          ...s,
          requirement: { ...s.requirement },
          effects: s.effects.map((e) => ({ ...e })),
        })),
      );
    },
    getDraft: editor.getDraft,
    isDirty: editor.isDirty,
    markPublished: editor.markPublished,
  };
}

// Local helpers.

function section(title: string, children: HTMLElement[]): HTMLElement {
  return el(
    "div",
    { class: "editor-section" },
    el("h3", {}, title),
    el("div", { class: "editor-grid" }, ...children),
  );
}

// Compact inline numeric input for the per-effect kind fields (different
// layout from form-fields' `numberField` which is full row).
function inlineNum(
  label: string,
  value: number,
  onChange: (v: number) => void,
  step?: number,
): HTMLElement {
  return el(
    "label",
    { class: "effect-num" },
    el("span", {}, label),
    el("input", {
      type: "number",
      value: String(value),
      step: step ? String(step) : "1",
      oninput: (e: Event) => {
        const v = Number.parseFloat((e.target as HTMLInputElement).value);
        if (Number.isFinite(v)) onChange(v);
      },
    }),
  );
}
